<template>
   <ValidationProvider
            :name="field"
            :rules="rules"
            v-slot="{ errors }"
            slim
        >
    <v-select
        v-model="selected"
        :label="$t(label)"
        :items="items"
        :clearable="clearable"
        :hide-details="hideErrorMessage"
        :menu-props="{ offsetY: true, contentClass: 'dropdown-filter-menu' }"
        :alvqa="alvqa"
        :multiple="multiple"
        item-color="generalCheckbox"
        @click:clear="resetVal()"
        :item-text="itemText"
        :item-value="itemValue"
        @input="additionalFunction()"
        :error-messages="errors"
        :disabled="disabled"
    >
        <!-- Items -->
        <!-- A custom item slot replaces Vuetify's default tile, so multiple has to redraw the checkbox -->
        <template v-slot:item="{ item, attrs, on }">
            <v-list-item v-if="multiple" v-bind="attrs" v-on="on">
                <v-list-item-action>
                    <v-icon :color="attrs.inputValue ? 'generalCheckbox' : undefined">
                        {{ attrs.inputValue ? 'check_box' : 'check_box_outline_blank' }}
                    </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                    <v-list-item-title :alvqa="item.alvqa">
                        {{ $alvPlugin.isLangConst(item[itemText]) ? $t(item[itemText]) : item[itemText] }}
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>
            <span v-else :alvqa="item.alvqa" @click="reselect(item)">
                {{ $alvPlugin.isLangConst(item[itemText]) ? $t(item[itemText]) : item[itemText] }}
            </span>
        </template>

        <!-- Selection -->
        <template v-slot:selection="{ item, index }">
            <div v-if="multiple">
                <span v-if="index === 0" :alvqa="item.alvqa">
                    {{ $alvPlugin.isLangConst(item[itemText]) ? $t(item[itemText]) : item[itemText] }}
                </span>
                <span v-if="index === 1">(+{{ selected.length - 1 }})</span>
            </div>
            <span v-else class="text-truncate" :alvqa="item.alvqa">
                {{ $alvPlugin.isLangConst(item[itemText]) ? $t(item[itemText]) : item[itemText] }}
            </span>
        </template>

        <!-- All select at the top -->
        <template v-if="allSelect" v-slot:prepend-item>
          <v-list-item
            ripple
            @click="toggle"
          >
            <v-list-item-action>
              <v-icon color="generalCheckbox" :alvqa="alvqa + '-checkbox-all-select'">
                {{ icon }}
              </v-icon>
                </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title :alvqa="alvqa + '-checkbox-all-select-text'">
                {{ $t('lang_GENERAL_CAPTION_SELECT_ALL') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
    </v-select>
    </ValidationProvider>

</template>

<script>
import VSelect from 'vuetify/es5/components/VSelect'

export default {
    name: "DropdownFilter",
    extends: VSelect,
    props: {
        context: String,
        label: String,
        items: Array,
        field: String,
        defaultValueNull: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: true
        },
        multiple: {
            type: Boolean,
            default: false
        },
        allSelect: {
            type: Boolean,
            default: false
        },
        alvqa: {
            type: String,
            default: ''
        },
        itemText: {
            type: String,
            default: 'text'
        },
        itemValue: {
            type: String,
            default: 'value'
        },
        additionalFunction:{
            type: Function,
            default: () => {}
        },
        rules: {
            type: [Object, String],
            default: ""
        },
        hideErrorMessage: {
            type: Boolean,
            default: true
        },
        // Options whose dialog has to stay reachable while the option is already selected
        runFunctionOnSameSelection: {
            type: Boolean,
            default: false
        },
    },
    computed: {
        selected: {
            get() { return this.$store.getters['getSelected' + this.context] },
            set(selectedOption) { this.$store.commit('setSelected' + this.context, selectedOption) }
        },
        allSelected() {
            let itemArrayWithoutHeaders = this.items
            itemArrayWithoutHeaders.forEach((element, index) => { 
                if('header' in element) { itemArrayWithoutHeaders.splice(index,1) }
            });

            return this.selected.length === itemArrayWithoutHeaders.length
        },
        icon () {
            if (this.allSelected) return 'check_box'
            return 'check_box_outline_blank'
        },
    },
    methods: {
        toggle () {
            this.$nextTick(() => {
                if (this.allSelected) {
                    this.selected = []
                } else {
                    this.selected = []
                    this.items.forEach(element => {
                        this.selected.push(element['value']) 
                    });
                }
            })
        },
        async resetVal() {
            await this.$nextTick()
            this.selected = this.defaultValueNull ? null : []
        },
        // Picking the selected option again emits no input event, so a dialog belonging to that
        // option would be out of reach once it is closed. Picking another option is left to the
        // input event alone, otherwise the dialog of the previous option opens on top of the new one
        reselect(item) {
            if (!this.runFunctionOnSameSelection) return
            if (item[this.itemValue] !== this.selected) return
            this.additionalFunction()
        }
    }
}
</script>

<style scoped>
::v-deep .v-select__selections {
    flex-wrap: nowrap;
    overflow: hidden;
}
</style>

<style>
.dropdown-filter-menu .v-list-item__action {
    margin-right: 16px !important;
}
</style>